'use client'

import { useState, useEffect } from 'react'
import { Search, ShoppingCart, User, Menu, X, Star, Heart } from 'lucide-react'
import Cart from '@/components/cart'

interface Category {
  id: string
  name: string
  description: string
  slug: string
}

interface Product {
  id: string
  name: string
  description: string
  price: number
  code: string
  image: string
  rating: number
  inStock: boolean
  category: Category
}

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [categories, setCategories] = useState<Category[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [isCartOpen, setIsCartOpen] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      // Use static data as primary source for immediate loading
      const staticCategories = [
        {
          id: '1',
          name: 'Fit Collection',
          description: 'Premium athletic wear',
          slug: 'fit'
        },
        {
          id: '2',
          name: 'Home Decor',
          description: 'Elegant home accessories',
          slug: 'home'
        },
        {
          id: '3',
          name: 'Golf Apparel',
          description: 'Performance golf wear',
          slug: 'golf'
        },
        {
          id: '4',
          name: 'Luxe Collection',
          description: 'Sophisticated luxury items',
          slug: 'luxe'
        }
      ]

      const staticProducts = [
        {
          id: '1',
          name: 'Premium Athletic Hoodie',
          description: 'Comfortable and stylish athletic hoodie',
          price: 89.99,
          code: 'ATH001',
          image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=400&fit=crop',
          rating: 4.5,
          inStock: true,
          category: { id: '1', name: 'Fit Collection', description: 'Premium athletic wear', slug: 'fit' }
        },
        {
          id: '2',
          name: 'Luxury Home Candle',
          description: 'Elegant scented candle for your home',
          price: 45.99,
          code: 'HOME001',
          image: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400&h=400&fit=crop',
          rating: 4.8,
          inStock: true,
          category: { id: '2', name: 'Home Decor', description: 'Elegant home accessories', slug: 'home' }
        },
        {
          id: '3',
          name: 'Performance Golf Polo',
          description: 'Premium golf shirt with moisture-wicking fabric',
          price: 79.99,
          code: 'GOLF001',
          image: 'https://images.unsplash.com/photo-1598808503746-f34c53b9323d?w=400&h=400&fit=crop',
          rating: 4.6,
          inStock: true,
          category: { id: '3', name: 'Golf Apparel', description: 'Performance golf wear', slug: 'golf' }
        },
        {
          id: '4',
          name: 'Designer Leather Wallet',
          description: 'Sophisticated leather wallet',
          price: 129.99,
          code: 'LUXE001',
          image: 'https://images.unsplash.com/photo-1627123424574-724758594e93?w=400&h=400&fit=crop',
          rating: 4.9,
          inStock: true,
          category: { id: '4', name: 'Luxe Collection', description: 'Sophisticated luxury items', slug: 'luxe' }
        }
      ]

      // Set static data immediately
      setCategories(staticCategories)
      setProducts(staticProducts)
      setLoading(false)

      // Then try to fetch from API in background (non-blocking)
      setTimeout(async () => {
        try {
          const [categoriesRes, productsRes] = await Promise.all([
            fetch('/api/categories').catch(() => null),
            fetch('/api/products').catch(() => null)
          ])
          
          if (categoriesRes?.ok) {
            const categoriesData = await categoriesRes.json()
            setCategories(categoriesData)
          }
          
          if (productsRes?.ok) {
            const productsData = await productsRes.json()
            setProducts(productsData)
          }
        } catch (error) {
          console.error('Background API fetch failed:', error)
          // Keep using static data
        }
      }, 0)
    } catch (error) {
      console.error('Error in fetchData:', error)
      setLoading(false)
    }
  }

  const categoryImages: Record<string, string> = {
    fit: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
    home: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop',
    golf: 'https://images.unsplash.com/photo-1592609931095-54a2168ae893?w=400&h=300&fit=crop',
    luxe: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop'
  }

  const categoryColors: Record<string, string> = {
    fit: 'from-gray-900 to-gray-700',
    home: 'from-amber-900 to-amber-700',
    golf: 'from-green-900 to-green-700',
    luxe: 'from-purple-900 to-purple-700'
  }

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category.slug === selectedCategory)

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-base-content/80">Loading KeeXperience Boutique...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-base-100 border-b border-base-300">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/logo.svg" 
                  alt="KeeXperience Boutique" 
                  width={40} 
                  height={40}
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-semibold text-base-content">KeeXperience</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8" aria-label="Main navigation">
              <a href="#home" className="text-base-content hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-2 py-1">Home</a>
              <a href="#categories" className="text-base-content hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-2 py-1">Categories</a>
              <a href="#products" className="text-base-content hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-2 py-1">Products</a>
              <a href="#about" className="text-base-content hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-2 py-1">About</a>
            </nav>

            {/* Search and Actions */}
            <div className="flex items-center space-x-4">
              <label className="input input-bordered flex items-center gap-2 hidden md:flex">
                <input type="text" className="grow" placeholder="Search products..." />
                <Search className="w-4 h-4 opacity-70" />
              </label>
              
              <button className="btn btn-ghost btn-circle">
                <Heart className="w-5 h-5" />
              </button>
              
              <button className="btn btn-ghost btn-circle" onClick={() => setIsCartOpen(true)}>
                <ShoppingCart className="w-5 h-5" />
              </button>
              
              <button className="btn btn-ghost btn-circle">
                <User className="w-5 h-5" />
              </button>

              {/* Mobile Menu Toggle */}
              <button 
                className="btn btn-ghost btn-circle md:hidden"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-base-100 border-t border-base-300">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <a href="#home" className="block text-base-content hover:text-primary transition-colors">Home</a>
              <a href="#categories" className="block text-base-content hover:text-primary transition-colors">Categories</a>
              <a href="#products" className="block text-base-content hover:text-primary transition-colors">Products</a>
              <a href="#about" className="block text-base-content hover:text-primary transition-colors">About</a>
              <label className="input input-bordered flex items-center gap-2">
                <input type="text" className="grow" placeholder="Search products..." />
                <Search className="w-4 h-4 opacity-70" />
              </label>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="relative bg-gradient-to-br from-base-200 to-base-100 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-base-content mb-6">
              KeeXperience Boutique
            </h1>
            <p className="text-xl text-base-content/80 mb-8">
              Rise and Shine, Timeless Sophistication
            </p>
            <p className="text-lg text-base-content/70 mb-12 max-w-2xl mx-auto">
              Discover luxury redefined with our curated collection of athletic wear, home decor, 
              golf apparel, and luxury replicas. Experience elegance in every detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="btn btn-primary btn-lg">
                Shop Now
              </button>
              <button className="btn btn-outline btn-primary btn-lg">
                Explore Collections
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="categories" className="py-16 bg-base-200">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-base-content mb-4">
              Shop by Category
            </h2>
            <p className="text-lg text-base-content/80">
              Explore our carefully curated collections
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => (
              <div key={category.id} className="card card-compact group cursor-pointer overflow-hidden shadow-xl bg-base-100 image-full">
                <figure>
                  <img
                    src={categoryImages[category.slug] || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop'}
                    alt={category.name}
                  />
                </figure>
                <div className="card-body items-center justify-center text-center">
                  <h2 className="card-title text-white">{category.name}</h2>
                  <p className="text-white opacity-90">{category.description}</p>
                  <div className="card-actions justify-end">
                    <button 
                      className="btn btn-primary"
                      onClick={() => setSelectedCategory(category.slug)}
                    >
                      Shop {category.name}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-base-content mb-4">
              Featured Products
            </h2>
            <p className="text-lg text-base-content/80 mb-8">
              Discover our handpicked selection of premium items
            </p>
            
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2">
              <button
                className={`btn btn-sm ${selectedCategory === 'all' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setSelectedCategory('all')}
              >
                All Products
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`btn btn-sm ${selectedCategory === category.slug ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setSelectedCategory(category.slug)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <div key={product.id} className="card card-compact group overflow-hidden shadow-xl bg-base-100">
                <figure className="relative h-64 overflow-hidden bg-base-200">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <button className="btn btn-ghost btn-circle bg-base-100/90 hover:bg-base-100 shadow-sm" onClick={(e) => { e.preventDefault(); }}>
                      <Heart className="w-4 h-4" />
                    </button>
                  </div>
                  {!product.inStock && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="badge badge-error text-error-content">Out of Stock</div>
                    </div>
                  )}
                </figure>
                <div className="card-body p-4 pb-2">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-semibold text-base text-base-content line-clamp-2 flex-1 pr-2">
                      {product.name}
                    </h3>
                    <span className="badge badge-outline text-xs">
                      {product.code}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="rating rating-sm">
                        {[...Array(5)].map((_, i) => (
                          <input
                            key={i}
                            type="radio"
                            name={`rating-${product.id}`}
                            className={`mask mask-star-2 ${i < Math.floor(product.rating) ? 'bg-warning' : 'bg-base-300'}`}
                            checked={i < Math.floor(product.rating) && i === Math.floor(product.rating) - 1}
                            readOnly
                          />
                        ))}
                      </div>
                      <span className="text-xs text-base-content/70 ml-1">({product.rating})</span>
                    </div>
                    <div className="badge badge-outline text-xs capitalize">
                      {product.category.slug}
                    </div>
                  </div>
                </div>
                <div className="p-4 pt-0">
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-bold text-primary">
                      ${product.price}
                    </p>
                    <span className="text-xs text-base-content/60">Free shipping</span>
                  </div>
                </div>
                <div className="card-actions p-4 pt-0">
                  <button className="btn btn-primary btn-sm w-full text-primary-content font-medium shadow-md hover:shadow-lg transition-all" onClick={(e) => { e.preventDefault(); }}>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 bg-base-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-base-content mb-8">
              About KeeXperience
            </h2>
            <p className="text-lg text-base-content/80 mb-8">
              At KeeXperience Boutique, we believe that luxury should be accessible to everyone who appreciates 
              quality craftsmanship and timeless design. Our carefully curated collections span from athletic wear 
              that empowers your active lifestyle to home decor that transforms your living space into a sanctuary.
            </p>
            <p className="text-lg text-base-content/80">
              Whether you're looking for performance golf apparel, sophisticated luxury replicas, or elegant home 
              accessories, each item in our collection is selected with the utmost attention to detail, quality, 
              and style. Rise and shine with KeeXperience – where sophistication meets everyday luxury.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral text-neutral-content py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <img 
                    src="/kxlogo.png" 
                    alt="KeeXperience Boutique" 
                    width={32} 
                    height={32}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-lg font-semibold">KeeXperience</span>
              </div>
              <p className="text-neutral-content/80">
                Rise and Shine, Timeless Sophistication
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Shop</h3>
              <ul className="space-y-2 text-neutral-content/80">
                <li><a href="#" className="hover:text-neutral-content transition-colors">Fit Collection</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Home Decor</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Golf Apparel</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Luxe Collection</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Customer Service</h3>
              <ul className="space-y-2 text-neutral-content/80">
                <li><a href="#" className="hover:text-neutral-content transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Shipping Info</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Returns</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Size Guide</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Connect</h3>
              <ul className="space-y-2 text-neutral-content/80">
                <li><a href="#" className="hover:text-neutral-content transition-colors">Instagram</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Facebook</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Twitter</a></li>
                <li><a href="#" className="hover:text-neutral-content transition-colors">Newsletter</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-neutral-focus mt-8 pt-8 text-center text-neutral-content/80">
            <p>&copy; 2024 KeeXperience Boutique. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Shopping Cart */}
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  )
}
